import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, OnInit, ChangeDetectorRef, NgZone } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DestinationVideoService } from '../../core/services/destination-video.service';
import { AccountService } from '../../core/services/account.service';
import { MemoryStorageService } from 'src/app/core/services/memory-storage.service';

// Use globally loaded Globe and Three from CDN / assets to avoid Webpack 4 ESM parse issues
declare const Globe: any;
declare const ResizeObserver: any;
declare const THREE: any;

export interface DestinationCardInfo {
  id: string;
  name: string;
  cardLine: string;
  bestTime: string;
  flightTime: string;
  tripDuration: string;
  estCost: string;
  monthlyEmi: string;
}

export const DESTINATION_CARDS: Record<string, DestinationCardInfo> = {
  'dubai': {
    id: 'dubai',
    name: 'UAE',
    cardLine: 'Burj Khalifa, desert safaris, endless shopping.',
    bestTime: 'Nov–Mar',
    flightTime: '~3–4 hrs',
    tripDuration: '4N trip',
    estCost: '₹55,000',
    monthlyEmi: '4,583'
  },
  'thailand': {
    id: 'thailand',
    name: 'Thailand',
    cardLine: 'Beaches, street food, island hops.',
    bestTime: 'Nov–Mar',
    flightTime: '~4 hrs',
    tripDuration: '4N trip',
    estCost: '₹45,000',
    monthlyEmi: '3,750'
  },
  'indonesia': {
    id: 'indonesia',
    name: 'Indonesia',
    cardLine: 'Bali temples, rice terraces, sunset villas.',
    bestTime: 'Apr–Oct',
    flightTime: '~6–8 hrs',
    tripDuration: '5N trip',
    estCost: '₹65,000',
    monthlyEmi: '5,417'
  },
  'singapore': {
    id: 'singapore',
    name: 'Singapore',
    cardLine: 'Sentosa, Gardens by the Bay, family fun.',
    bestTime: 'Feb–Apr',
    flightTime: '~5 hrs',
    tripDuration: '4N trip',
    estCost: '₹60,000',
    monthlyEmi: '5,000'
  },
  'vietnam': {
    id: 'vietnam',
    name: 'Vietnam',
    cardLine: 'Ha Long Bay, Hoi An lanterns, great value.',
    bestTime: 'Nov–Apr',
    flightTime: '~5–6 hrs',
    tripDuration: '5N trip',
    estCost: '₹50,000',
    monthlyEmi: '4,167'
  },
  'japan': {
    id: 'japan',
    name: 'Japan',
    cardLine: 'Cherry blossoms, bullet trains, Tokyo nights.',
    bestTime: 'Mar–May, Oct–Nov',
    flightTime: '~8–9 hrs',
    tripDuration: '6N trip',
    estCost: '₹1.4L',
    monthlyEmi: '11,667'
  },
  'south-india': {
    id: 'south-india',
    name: 'Kerala',
    cardLine: 'Backwaters, houseboats, Munnar tea hills.',
    bestTime: 'Sep–Mar',
    flightTime: '~2 hrs from Mumbai',
    tripDuration: '4N trip',
    estCost: '₹30,000',
    monthlyEmi: '2,500'
  },
  'europe-1': {
    id: 'europe-1',
    name: 'Western Europe',
    cardLine: 'Eiffel Tower, Swiss Alps, Amsterdam canals.',
    bestTime: 'May–Sep',
    flightTime: '~9–10 hrs',
    tripDuration: '6N trip',
    estCost: '₹1.8L',
    monthlyEmi: '15,000'
  },
  'europe-2': {
    id: 'europe-2',
    name: 'Southern Europe',
    cardLine: 'Rome, Santorini, Barcelona — sun, sea, gelato.',
    bestTime: 'Apr–Jun, Sep–Oct',
    flightTime: '~8–9 hrs',
    tripDuration: '5N trip',
    estCost: '₹1.5L',
    monthlyEmi: '12,500'
  },
  'hong-kong': {
    id: 'hong-kong',
    name: 'Hong Kong',
    cardLine: 'Harbour lights, Disneyland, dim sum nights.',
    bestTime: 'Oct–Dec',
    flightTime: '~5–6 hrs',
    tripDuration: '4N trip',
    estCost: '₹70,000',
    monthlyEmi: '5,833'
  },
  'mauritius': {
    id: 'mauritius',
    name: 'Mauritius',
    cardLine: 'Turquoise lagoons, waterfalls, beach resorts.',
    bestTime: 'May–Dec',
    flightTime: '~6 hrs',
    tripDuration: '5N trip',
    estCost: '₹95,000',
    monthlyEmi: '7,917'
  },
  'sri-lanka': {
    id: 'sri-lanka',
    name: 'Sri Lanka',
    cardLine: 'Tea hills, beaches, wildlife — next door.',
    bestTime: 'Dec–Mar',
    flightTime: '~2–3 hrs',
    tripDuration: '4N trip',
    estCost: '₹40,000',
    monthlyEmi: '3,333'
  },
  'malaysia': {
    id: 'malaysia',
    name: 'Malaysia',
    cardLine: 'Petronas Towers, Langkawi, Genting highs.',
    bestTime: 'Dec–Apr',
    flightTime: '~5 hrs',
    tripDuration: '4N trip',
    estCost: '₹48,000',
    monthlyEmi: '4,000'
  },
  'goa': {
    id: 'goa',
    name: 'Goa',
    cardLine: 'Beaches, shacks, sunsets, old Portuguese charm.',
    bestTime: 'Nov–Feb',
    flightTime: '~1–2.5 hrs',
    tripDuration: '3N trip',
    estCost: '₹25,000',
    monthlyEmi: '2,083'
  },
  'north-india': {
    id: 'north-india',
    name: 'Ladakh and Kashmir',
    cardLine: 'Pangong Lake, Dal Lake, snow peaks.',
    bestTime: 'May–Sep',
    flightTime: '~1.5 hrs from Delhi',
    tripDuration: '5N trip',
    estCost: '₹35,000',
    monthlyEmi: '2,917'
  },
  'chardham': {
    id: 'chardham',
    name: 'CharDham',
    cardLine: 'Kedarnath, Badrinath, Gangotri & Yamunotri yatra await.',
    bestTime: 'May–Jun, Sep–Oct',
    flightTime: 'Helicopter / road from Dehradun',
    tripDuration: '6N yatra',
    estCost: '₹45,000',
    monthlyEmi: '3,750'
  },
  'andaman': {
    id: 'andaman',
    name: 'Andaman Islands',
    cardLine: 'Havelock beaches, coral reefs, scuba diving.',
    bestTime: 'Oct–May',
    flightTime: '~2.5 hrs from Chennai',
    tripDuration: '5N trip',
    estCost: '₹42,000',
    monthlyEmi: '3,500'
  },
  'mumbai': {
    id: 'mumbai',
    name: 'Cordelia Cruises',
    cardLine: 'Gateway of India, Arabian Sea luxury cruise, scenic ocean horizons.',
    bestTime: 'Oct–May',
    flightTime: 'Mumbai Port Departure',
    tripDuration: '3N / 4N Cruise',
    estCost: '₹35,000',
    monthlyEmi: '2,917'
  },
  'global': {
    id: 'global',
    name: 'Any Destination Worldwide',
    cardLine: 'Pick your destination later. Use funds for flights, hotels, or visas anywhere.',
    bestTime: 'Anytime in next 6 months',
    flightTime: 'Fly with any airline globally',
    tripDuration: 'Custom trip up to ₹5L',
    estCost: '₹50,000 – ₹5,00,000',
    monthlyEmi: '4,500'
  }
};

// Global memory cache for GeoJSON polygons so back/forward navigation takes 0ms
let cachedProcessedPolygons: any[] | null = null;
let pendingPolygonsPromise: Promise<any[]> | null = null;

function loadPolygonsData(): Promise<any[]> {
  if (cachedProcessedPolygons) {
    return Promise.resolve(cachedProcessedPolygons);
  }
  if (pendingPolygonsPromise) {
    return pendingPolygonsPromise;
  }
  pendingPolygonsPromise = Promise.all([
    fetch('assets/globe/ne_110m_admin_0_countries.json').then(res => res.json()),
    fetch('assets/globe/missing-countries.json').then(res => res.json()),
    fetch('assets/globe/india-rewound.json').then(res => res.json())
  ]).then(([countries, missingCountries, india]) => {
    const otherCountries: any[] = [];
    countries.features.forEach((f: any) => {
      if (f.properties?.NAME === 'India') {
        return;
      }
      if (f.properties?.NAME === 'France' && f.geometry?.type === 'MultiPolygon') {
        const europeanCoords: any[] = [];
        const overseasCoords: any[] = [];
        f.geometry.coordinates.forEach((poly: any) => {
          const ring = poly[0] || [];
          const avgLng = ring.reduce((sum: number, pt: number[]) => sum + pt[0], 0) / (ring.length || 1);
          const avgLat = ring.reduce((sum: number, pt: number[]) => sum + pt[1], 0) / (ring.length || 1);
          if (avgLng < -20 && avgLat > -10 && avgLat < 20) {
            overseasCoords.push(poly);
          } else {
            europeanCoords.push(poly);
          }
        });
        otherCountries.push({
          ...f,
          geometry: {
            ...f.geometry,
            coordinates: europeanCoords
          }
        });
        if (overseasCoords.length > 0) {
          otherCountries.push({
            type: 'Feature',
            properties: {
              ...f.properties,
              NAME: 'French Guiana',
              ADMIN: 'French Guiana',
              SOVEREIGNT: 'France'
            },
            geometry: {
              type: 'MultiPolygon',
              coordinates: overseasCoords
            }
          });
        }
      } else {
        otherCountries.push(f);
      }
    });

    let indiaFeatures: any[] = [];
    if (india.type === 'FeatureCollection') {
      indiaFeatures = india.features;
    } else if (india.type === 'GeometryCollection') {
      indiaFeatures = india.geometries.map((g: any) => ({ type: 'Feature', geometry: g, properties: {} }));
    } else {
      indiaFeatures = [{ type: 'Feature', geometry: india.geometry || india, properties: {} }];
    }

    indiaFeatures.forEach((f: any) => {
      if (!f.properties) f.properties = {};
      f.properties.NAME = 'India';
    });

    const smallCountries = missingCountries.features || [];
    cachedProcessedPolygons = [...otherCountries, ...smallCountries, ...indiaFeatures];
    return cachedProcessedPolygons;
  }).catch(err => {
    pendingPolygonsPromise = null;
    throw err;
  });

  return pendingPolygonsPromise;
}

function preloadImage(src: string): Promise<void> {
  return new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });
}

function waitForGlobeConstructor(): Promise<any> {
  return new Promise((resolve) => {
    if (typeof (window as any).Globe === 'function') {
      return resolve((window as any).Globe);
    }
    let tries = 0;
    const interval = setInterval(() => {
      tries++;
      if (typeof (window as any).Globe === 'function') {
        clearInterval(interval);
        resolve((window as any).Globe);
      } else if (tries > 60) {
        clearInterval(interval);
        resolve((window as any).Globe || null);
      }
    }, 50);
  });
}

@Component({
  selector: 'app-travel-destination-explorer',
  templateUrl: './travel-destination-explorer.component.html',
  styleUrls: ['./travel-destination-explorer.component.scss']
})
export class TravelDestinationExplorerComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('globeContainer') globeContainer!: ElementRef;
  globe: any;
  selectedLocation: any = null;
  resizeObserver: any;
  public isGlobeLoading: boolean = true;
  public isDarkMode: boolean = true; // Default to Dark mode (which looks best for 3D globe)
  public isSpinningSurprise: boolean = false;

  private pageShowListener: any = null;
  private popStateListener: any = null;
  private globeInitGeneration: number = 0;

  agentName: string = 'Travel Partner';

  // Typewriter rotation state
  rotatingDestinations: string[] = ['Cordelia Cruises', 'Bali', 'Dubai', 'Paris', 'Thailand', 'Kashmir', 'Kerala'];
  currentRotatingWord: string = 'Bali';
  typewriterText: string = 'Your Bali trip starts here.';
  private rotatingIndex: number = 0;
  private charIndex: number = 4;
  private isDeletingWord: boolean = true;
  private typewriterTimeout: any = null;

  locations = [
    { id: 'mumbai', name: 'Cordelia Cruises', lat: 19.0760, lng: 72.8777, size: 0.6, color: '#00ffff', isShip: true },
    { id: 'japan', name: 'Japan', lat: 36.2048, lng: 138.2529, size: 0.5, color: '#00ffff' },
    { id: 'europe-1', name: 'Western Europe', lat: 49.8175, lng: 6.5, size: 0.5, color: '#00ffff' },
    { id: 'goa', name: 'Goa', lat: 15.2993, lng: 74.1240, size: 0.5, color: '#00ffff' },
    { id: 'dubai', name: 'UAE', lat: 25.2048, lng: 55.2708, size: 0.5, color: '#00ffff' },
    { id: 'singapore', name: 'Singapore', lat: 1.3521, lng: 103.8198, size: 0.5, color: '#00ffff' },
    { id: 'north-india', name: 'Ladakh and Kashmir', lat: 34.1526, lng: 77.5771, size: 0.5, color: '#00ffff' },
    { id: 'indonesia', name: 'Indonesia', lat: -8.3405, lng: 115.0920, size: 0.5, color: '#00ffff' },
    { id: 'europe-2', name: 'Southern Europe', lat: 45.4408, lng: 9.8, size: 0.5, color: '#00ffff' },
    { id: 'thailand', name: 'Thailand', lat: 15.8700, lng: 100.9925, size: 0.5, color: '#00ffff' },
    { id: 'chardham', name: 'CharDham', lat: 30.7346, lng: 79.0669, size: 0.5, color: '#00ffff' },
    { id: 'sri-lanka', name: 'Sri Lanka', lat: 7.8731, lng: 80.7718, size: 0.5, color: '#00ffff' },
    { id: 'vietnam', name: 'Vietnam', lat: 14.0583, lng: 108.2772, size: 0.5, color: '#00ffff' },
    { id: 'mauritius', name: 'Mauritius', lat: -20.3484, lng: 57.5522, size: 0.5, color: '#00ffff' },
    { id: 'south-india', name: 'Kerala', lat: 10.0889, lng: 77.0595, size: 0.5, color: '#00ffff' },
    { id: 'hong-kong', name: 'Hong Kong', lat: 22.3193, lng: 114.1694, size: 0.5, color: '#00ffff' },
    { id: 'andaman', name: 'Andaman Islands', lat: 11.7401, lng: 92.6586, size: 0.5, color: '#00ffff' },
    { id: 'malaysia', name: 'Malaysia', lat: 4.2105, lng: 101.9758, size: 0.5, color: '#00ffff' }
  ];

  private queryParams: any = {};
  private stopRotationListener: (() => void) | null = null;
  private hoverEnterListener: (() => void) | null = null;
  private hoverLeaveListener: (() => void) | null = null;
  private userManuallyInteracted: boolean = false;
  private planesGroup: any = null;
  private planeObjects: any[] = [];
  private activeFlights: any[] = [];
  private currentFlightRouteIndex: number = 0;
  private lastFlightLaunchTime: number = 0;
  private planeAnimFrameId: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private destinationVideoService: DestinationVideoService,
    private accountService: AccountService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit(): void {
    this.isGlobeLoading = true;
    MemoryStorageService.clear();

    // Listen to browser pageshow (handles bfcache restorations when user presses back button from forward pages)
    if (typeof window !== 'undefined') {
      this.pageShowListener = (event: any) => {
        if (event && (event.persisted || !this.globe || !this.globeContainer?.nativeElement?.querySelector('canvas'))) {
          this.ngZone.run(() => {
            this.isGlobeLoading = true;
            this.cdr.detectChanges();
            this.initGlobe();
          });
        }
      };
      window.addEventListener('pageshow', this.pageShowListener);

      // Listen to browser history popstate navigation
      this.popStateListener = () => {
        if (!this.globe || !this.globeContainer?.nativeElement?.querySelector('canvas')) {
          this.ngZone.run(() => {
            this.isGlobeLoading = true;
            this.cdr.detectChanges();
            this.initGlobe();
          });
        }
      };
      window.addEventListener('popstate', this.popStateListener);
    }

    // Capture all incoming query params to forward them to signup
    this.route.queryParams.subscribe(async params => {
      this.queryParams = { ...params };
      const regId = params['regid'] || params['regId'] || params['RegID'] || params['RegId'] || params['utm_source'];
      if (regId && regId.trim()) {
        try {
          const agentDetails: any = await this.accountService.getAgentDetails(regId.trim().toUpperCase()).toPromise();
          if (agentDetails && (agentDetails.brandName || agentDetails.ownerDirectorName)) {
            this.agentName = agentDetails.brandName || agentDetails.ownerDirectorName;
          } else {
            this.agentName = 'Travel Partner';
          }
        } catch (err) {
          console.warn('Could not fetch agent details for regid:', regId, err);
          this.agentName = 'Travel Partner';
        }
      } else {
        this.agentName = params['agentName'] || params['agent'] || params['travelBrandName'] || 'Travel Partner';
      }
      this.cdr.detectChanges();
    });
    this.updateBodyTheme();
    this.initTypewriter();
  }

  initTypewriter(): void {
    if (this.typewriterTimeout) {
      clearTimeout(this.typewriterTimeout);
    }

    const typeNext = () => {
      this.ngZone.run(() => {
        const fullWord = this.rotatingDestinations[this.rotatingIndex];

        if (this.isDeletingWord) {
          this.charIndex--;
          if (this.charIndex <= 0) {
            this.charIndex = 0;
            this.currentRotatingWord = '';
            this.typewriterText = `Your ${this.currentRotatingWord} trip starts here.`;
            this.isDeletingWord = false;
            this.rotatingIndex = (this.rotatingIndex + 1) % this.rotatingDestinations.length;
            this.cdr.detectChanges();
            this.typewriterTimeout = setTimeout(typeNext, 350);
            return;
          } else {
            this.currentRotatingWord = fullWord.substring(0, this.charIndex);
            this.typewriterText = `Your ${this.currentRotatingWord} trip starts here.`;
            this.cdr.detectChanges();
            this.typewriterTimeout = setTimeout(typeNext, 50);
            return;
          }
        } else {
          this.charIndex++;
          if (this.charIndex >= fullWord.length) {
            this.charIndex = fullWord.length;
            this.currentRotatingWord = fullWord;
            this.typewriterText = `Your ${this.currentRotatingWord} trip starts here.`;
            this.isDeletingWord = true;
            this.cdr.detectChanges();
            this.typewriterTimeout = setTimeout(typeNext, 2000);
            return;
          } else {
            this.currentRotatingWord = fullWord.substring(0, this.charIndex);
            this.typewriterText = `Your ${this.currentRotatingWord} trip starts here.`;
            this.cdr.detectChanges();
            this.typewriterTimeout = setTimeout(typeNext, 90);
            return;
          }
        }
      });
    };

    // Hold initial "Bali" for 1.8 seconds before beginning deletion
    this.typewriterTimeout = setTimeout(typeNext, 1800);
  }

  getArcsData(): any[] {
    const mumbaiLat = 19.0760;
    const mumbaiLng = 72.8777;
    return this.locations
      .filter(loc => loc.id !== 'mumbai')
      .map((loc, idx) => {
        const dist = Math.hypot(loc.lat - mumbaiLat, loc.lng - mumbaiLng);
        const altitude = Math.min(0.42, Math.max(0.12, dist * 0.0035 + 0.12));
        return {
          startLat: mumbaiLat,
          startLng: mumbaiLng,
          endLat: loc.lat,
          endLng: loc.lng,
          order: idx,
          altitude: altitude,
          id: loc.id,
          name: loc.name
        };
      });
  }

  getDestinationCard(id: string): DestinationCardInfo {
    if (id && DESTINATION_CARDS[id]) {
      return DESTINATION_CARDS[id];
    }
    const name = this.selectedLocation?.name || 'Selected Destination';
    return {
      id: id || 'custom',
      name: name,
      cardLine: 'Your unforgettable getaway awaits.',
      bestTime: 'Year-round',
      flightTime: 'Direct & connecting flights',
      tripDuration: '5N trip',
      estCost: '₹1.0L',
      monthlyEmi: '8,333'
    };
  }

  deselectLocation(): void {
    this.selectedLocation = null;
    this.userManuallyInteracted = false;
    this.updateSelectedMarkerUI();
    const ctrl = this.globe?.controls();
    if (ctrl) {
      ctrl.autoRotate = true;
      ctrl.autoRotateSpeed = 0.5;
    }
    const currentAlt = window.innerWidth <= 768 ? 2.5 : 2.0;
    if (this.globe) {
      this.globe.pointOfView({ lat: 21.0, lng: 78.5, altitude: currentAlt }, 1000);
    }
  }

  setTheme(dark: boolean): void {
    if (this.isDarkMode === dark) return;
    this.isDarkMode = dark;
    this.applyTheme();
    this.cdr.detectChanges();
  }

  toggleTheme(): void {
    this.setTheme(!this.isDarkMode);
  }

  private updateBodyTheme(): void {
    if (typeof document !== 'undefined' && document.body) {
      if (this.isDarkMode) {
        document.body.classList.add('globe-dark-theme');
        document.body.classList.remove('globe-light-theme');
      } else {
        document.body.classList.add('globe-light-theme');
        document.body.classList.remove('globe-dark-theme');
      }
    }
  }

  applyTheme(): void {
    this.updateBodyTheme();
    if (!this.globe) return;

    const highlightedCountries = [
      'Japan', 'Malaysia', 'Vietnam', 'Singapore', 'Indonesia',
      'France', 'Czechia', 'Netherlands', 'Switzerland', 'Italy',
      'Thailand', 'United Arab Emirates', 'Sri Lanka', 'Mauritius',
      'Hong Kong', 'India',
    ];

    if (this.isDarkMode) {
      this.globe
        .globeImageUrl('assets/globe/earth-dark.jpg')
        .backgroundImageUrl(null as any)
        .backgroundColor('rgba(0, 0, 0, 0)')
        .showAtmosphere(true)
        .atmosphereColor('lightskyblue')
        .atmosphereAltitude(0.15)
        .polygonCapColor((d: any) => highlightedCountries.includes(d.properties?.NAME) ? 'rgba(48, 164, 197, 0.8)' : null)
        .polygonStrokeColor((d: any) => {
          if (highlightedCountries.includes(d.properties?.NAME)) return '#ffcc03';
          return 'rgba(255, 255, 255, 0.4)';
        })
        .ringColor(() => (t: number) => `rgba(255, 204, 3, ${Math.max(0, (1 - t) * 0.95)})`);
    } else {
      this.globe
        .globeImageUrl('assets/globe/earth-light.jpg')
        .backgroundImageUrl(null as any)
        .backgroundColor('#f7f6f2')
        .showAtmosphere(false)
        .polygonCapColor((d: any) => highlightedCountries.includes(d.properties?.NAME) ? '#f5c538' : '#f5f4f0')
        .polygonStrokeColor((d: any) => {
          if (highlightedCountries.includes(d.properties?.NAME)) return '#333333';
          return 'rgba(50, 50, 50, 0.65)';
        })
        .ringColor(() => (t: number) => `rgba(2, 132, 199, ${Math.max(0, (1 - t) * 0.95)})`);
    }

    if (this.planeObjects && this.planeObjects.length) {
      this.initFlyingPlanes();
    }
  }

  ngAfterViewInit(): void {
    this.initGlobe();
  }

  async initGlobe(): Promise<void> {
    if (!this.globeContainer || !this.globeContainer.nativeElement) {
      return;
    }

    const container = this.globeContainer.nativeElement;
    const generation = ++this.globeInitGeneration;

    // Ensure loader is showing
    this.isGlobeLoading = true;
    this.cdr.detectChanges();

    // Clean up any existing instance or leftover canvas in container
    if (this.globe) {
      try {
        this.globe._destructor();
      } catch (e) { }
      this.globe = null;
    }
    container.innerHTML = '';

    const highlightedCountries = [
      'Japan', 'Malaysia', 'Vietnam', 'Singapore', 'Indonesia',
      'France', 'Czechia', 'Netherlands', 'Switzerland', 'Italy',
      'Thailand', 'United Arab Emirates', 'Sri Lanka', 'Mauritius',
      'Hong Kong', 'India'
    ];

    try {
      const GlobeConstructor = await waitForGlobeConstructor();
      if (!GlobeConstructor) {
        throw new Error('Globe.gl library unavailable');
      }

      if (generation !== this.globeInitGeneration) return;

      const textureUrl = this.isDarkMode ? 'assets/globe/earth-dark.jpg' : 'assets/globe/earth-light.jpg';

      let globeReadyTriggered = false;
      let onReadyCallback: (() => void) | null = null;
      const globeReadyPromise = new Promise<void>((resolve) => {
        onReadyCallback = () => {
          if (!globeReadyTriggered) {
            globeReadyTriggered = true;
            resolve();
          }
        };
      });

      this.globe = GlobeConstructor()(container)
        .onGlobeReady(() => {
          if (onReadyCallback) onReadyCallback();
        })
        .globeImageUrl(textureUrl)
        .backgroundImageUrl(null as any)
        .backgroundColor(this.isDarkMode ? 'rgba(0, 0, 0, 0)' : '#f7f6f2')
        .showAtmosphere(this.isDarkMode)
        .atmosphereColor(this.isDarkMode ? 'lightskyblue' : 'transparent')
        .atmosphereAltitude(this.isDarkMode ? 0.15 : 0.0)
        // 3D Animated Sonar Surface Waves around destination markers
        .ringsData(this.locations)
        .ringLat((d: any) => d.lat)
        .ringLng((d: any) => d.lng)
        .ringAltitude(0.005)
        .ringColor(() => (t: number) => {
          if (this.isDarkMode) {
            return `rgba(255, 204, 3, ${Math.max(0, (1 - t) * 0.95)})`;
          } else {
            return `rgba(2, 132, 199, ${Math.max(0, (1 - t) * 0.95)})`;
          }
        })
        .ringMaxRadius(3.5)
        .ringPropagationSpeed(1.8)
        .ringRepeatPeriod(900)
        .htmlElementsData(this.locations)
        .htmlElement((d: any) => {
          const el = document.createElement('div');
          el.className = 'location-marker';
          el.setAttribute('data-id', d.id);
          if (this.selectedLocation && this.selectedLocation.id === d.id) {
            el.classList.add('is-selected');
          }

          const pulse = document.createElement('div');
          pulse.className = 'marker-pulse';

          const pulseHover = document.createElement('div');
          pulseHover.className = 'marker-pulse-hover';

          const core = document.createElement('div');

          if (d.id === 'mumbai' || d.isShip) {
            el.classList.add('ship-marker');
            core.className = 'ship-core';
            core.style.width = '24px';
            core.style.height = '24px';
            core.style.minWidth = '24px';
            core.style.minHeight = '24px';
            core.innerHTML = `
              <svg class="ship-icon" viewBox="0 0 32 32" width="18" height="18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <!-- Water wave -->
                <path d="M3 24C6.5 26.5 10.5 26.5 14 24C17.5 21.5 21.5 21.5 25 24C27.5 25.8 29.5 25.5 31 24" stroke="#00ffff" stroke-width="2" stroke-linecap="round"/>
                <!-- Cruise ship Hull -->
                <path d="M4.5 21.5L6.8 15.5H25.2L27.5 21.5C23.5 20 19.5 20 16 22C12.5 23.5 8.5 23.5 4.5 21.5Z" fill="url(#shipHullGrad)" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round"/>
                <!-- Mid Promenade Deck -->
                <path d="M8.5 15.5V10.5H23.5V15.5" fill="#ffffff" stroke="#002d3a" stroke-width="0.8"/>
                <!-- Upper Bridge Deck -->
                <path d="M11.5 10.5V7H20.5V10.5" fill="#f8fafc" stroke="#002d3a" stroke-width="0.8"/>
                <!-- Cabin Windows -->
                <circle cx="11" cy="13" r="0.9" fill="#002d3a"/>
                <circle cx="14" cy="13" r="0.9" fill="#002d3a"/>
                <circle cx="17" cy="13" r="0.9" fill="#002d3a"/>
                <circle cx="20" cy="13" r="0.9" fill="#002d3a"/>
                <circle cx="14" cy="8.8" r="0.75" fill="#002d3a"/>
                <circle cx="17" cy="8.8" r="0.75" fill="#002d3a"/>
                <!-- Red Funnel / Smokestack with Gold pennant -->
                <rect x="15.5" y="4.2" width="2.8" height="2.8" rx="0.5" fill="#ef4444"/>
                <path d="M18.3 5.4H20.8L19.5 4.2H18.3V5.4Z" fill="#ffcc03"/>
                <defs>
                  <linearGradient id="shipHullGrad" x1="4.5" y1="15.5" x2="27.5" y2="21.5" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#ffffff"/>
                    <stop offset="0.55" stop-color="#e2e8f0"/>
                    <stop offset="1" stop-color="#0284c7"/>
                  </linearGradient>
                </defs>
              </svg>
            `;
          } else {
            core.className = 'marker-core';
          }

          const tooltip = document.createElement('div');
          tooltip.className = 'marker-tooltip';

          const thumbWrap = document.createElement('div');
          thumbWrap.className = 'marker-tooltip-thumb-wrap';

          const thumb = document.createElement('img');
          thumb.className = 'marker-tooltip-thumb';
          thumb.src = `assets/videos/${d.id}/desktop.webp`;
          thumb.alt = d.name;
          thumb.loading = 'lazy';
          thumb.onerror = () => {
            thumbWrap.style.display = 'none';
          };

          thumbWrap.appendChild(thumb);

          const title = document.createElement('span');
          title.className = 'marker-tooltip-title';
          title.innerText = d.name;

          tooltip.appendChild(thumbWrap);
          tooltip.appendChild(title);

          el.appendChild(pulse);
          el.appendChild(pulseHover);
          el.appendChild(core);
          el.appendChild(tooltip);

          el.onclick = (e) => {
            e.stopPropagation();
            this.onLocationClick(d);
          };
          return el;
        })
        .polygonAltitude((d: any) => d.properties?.NAME === 'India' ? 0.007 : 0.005)
        .polygonCapColor((d: any) => {
          if (highlightedCountries.includes(d.properties?.NAME)) {
            return this.isDarkMode ? 'rgba(48, 164, 197, 0.85)' : '#f5c538';
          }
          return this.isDarkMode ? null : '#f5f4f0';
        })
        .polygonSideColor(() => null)
        .polygonStrokeColor((d: any) => {
          if (this.isDarkMode) {
            if (highlightedCountries.includes(d.properties?.NAME)) return '#ffcc03';
            return 'rgba(255, 255, 255, 0.4)';
          } else {
            if (highlightedCountries.includes(d.properties?.NAME)) return '#333333';
            return 'rgba(50, 50, 50, 0.65)';
          }
        })
        .onPolygonClick((d: any, event: any, coords?: any) => {
          // Proximity detection for small island destinations where 110m polygon click may hit adjacent countries
          if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
            // Check proximity to Singapore (lat 1.3521, lng 103.8198)
            const distSingapore = Math.hypot(coords.lat - 1.3521, coords.lng - 103.8198);
            if (distSingapore < 2.5) {
              const sgLoc = this.locations.find(l => l.id === 'singapore');
              if (sgLoc) {
                this.onLocationClick(sgLoc);
                return;
              }
            }

            // Check proximity to Hong Kong (lat 22.3193, lng 114.1694)
            const distHK = Math.hypot(coords.lat - 22.3193, coords.lng - 114.1694);
            if (distHK < 2.0) {
              const hkLoc = this.locations.find(l => l.id === 'hong-kong');
              if (hkLoc) {
                this.onLocationClick(hkLoc);
                return;
              }
            }

            // Check proximity to Mauritius (lat -20.3484, lng 57.5522)
            const distMu = Math.hypot(coords.lat - (-20.3484), coords.lng - 57.5522);
            if (distMu < 3.0) {
              const muLoc = this.locations.find(l => l.id === 'mauritius');
              if (muLoc) {
                this.onLocationClick(muLoc);
                return;
              }
            }

            // Check proximity to Andaman (lat 11.7401, lng 92.6586)
            const distAn = Math.hypot(coords.lat - 11.7401, coords.lng - 92.6586);
            if (distAn < 2.5) {
              const anLoc = this.locations.find(l => l.id === 'andaman');
              if (anLoc) {
                this.onLocationClick(anLoc);
                return;
              }
            }
          }

          const countryName = d?.properties?.NAME;
          if (!countryName) return;

          // If Indonesia polygon (Riau islands) was clicked near Singapore coordinates
          if (countryName === 'Indonesia' && coords && coords.lat > 0 && coords.lng > 102 && coords.lng < 106) {
            const sgLoc = this.locations.find(l => l.id === 'singapore');
            if (sgLoc) {
              this.onLocationClick(sgLoc);
              return;
            }
          }

          let loc: any = null;
          if (countryName === 'Japan') loc = this.locations.find(l => l.id === 'japan');
          else if (countryName === 'Malaysia') loc = this.locations.find(l => l.id === 'malaysia');
          else if (countryName === 'Vietnam') loc = this.locations.find(l => l.id === 'vietnam');
          else if (countryName === 'Singapore') loc = this.locations.find(l => l.id === 'singapore');
          else if (countryName === 'Indonesia') loc = this.locations.find(l => l.id === 'indonesia');
          else if (['France', 'Czechia', 'Netherlands'].includes(countryName)) loc = this.locations.find(l => l.id === 'europe-1');
          else if (countryName === 'Thailand') loc = this.locations.find(l => l.id === 'thailand');
          else if (['Switzerland', 'Italy'].includes(countryName)) loc = this.locations.find(l => l.id === 'europe-2');
          else if (countryName === 'United Arab Emirates') loc = this.locations.find(l => l.id === 'dubai');
          else if (countryName === 'Sri Lanka') loc = this.locations.find(l => l.id === 'sri-lanka');
          else if (countryName === 'Mauritius') loc = this.locations.find(l => l.id === 'mauritius');
          else if (countryName === 'Hong Kong') loc = this.locations.find(l => l.id === 'hong-kong');
          else if (countryName === 'India') {
            if (coords && typeof coords.lat === 'number' && typeof coords.lng === 'number') {
              const indiaLocations = this.locations.filter(l => ['north-india', 'chardham', 'goa', 'south-india'].includes(l.id));
              let closestLoc = indiaLocations[0];
              let minDist = Infinity;
              for (const l of indiaLocations) {
                const d = Math.hypot(coords.lat - l.lat, coords.lng - l.lng);
                if (d < minDist) {
                  minDist = d;
                  closestLoc = l;
                }
              }
              loc = closestLoc;
            } else {
              loc = this.locations.find(l => l.id === 'north-india');
            }
          }

          if (loc) {
            this.onLocationClick(loc);
          }
        });

      // Start the globe facing India
      const initialAlt = window.innerWidth <= 768 ? 2.5 : 2.0;
      this.globe.pointOfView({ lat: 21.0, lng: 78.5, altitude: initialAlt }, 0);

      // Default continuous auto-rotation
      const controls = this.globe.controls();
      if (controls) {
        controls.autoRotate = true;
        controls.autoRotateSpeed = 0.6;
        controls.enableZoom = true;
        controls.addEventListener('start', () => {
          controls.autoRotate = false;
          this.userManuallyInteracted = true;
        });
      }

      // Stop auto-rotation once user touches, presses, or drags on the globe or background
      this.stopRotationListener = () => {
        this.userManuallyInteracted = true;
        const ctrl = this.globe?.controls();
        if (ctrl) {
          ctrl.autoRotate = false;
        }
      };
      container.addEventListener('pointerdown', this.stopRotationListener, { passive: true });
      container.addEventListener('touchstart', this.stopRotationListener, { passive: true });
      container.addEventListener('mousedown', this.stopRotationListener, { passive: true });
      this.globe.onGlobeClick(() => {
        this.userManuallyInteracted = true;
        const ctrl = this.globe?.controls();
        if (ctrl) {
          ctrl.autoRotate = false;
        }
      });

      // Polling fallback in case onGlobeReady already fired on cached texture
      const sceneVisibleInterval = setInterval(() => {
        if (this.globe?.scene()?.visible) {
          clearInterval(sceneVisibleInterval);
          if (onReadyCallback) onReadyCallback();
        }
      }, 50);

      // Concurrently load polygons, texture, and sky backgrounds
      const [polygons] = await Promise.all([
        loadPolygonsData(),
        preloadImage(textureUrl),
        preloadImage(this.isDarkMode ? 'assets/globe/night-sky.png' : 'assets/globe/day-sky.png'),
        globeReadyPromise
      ]);

      clearInterval(sceneVisibleInterval);

      if (generation !== this.globeInitGeneration) return;

      // Apply polygons
      if (this.globe && polygons) {
        this.globe.polygonsData(polygons);
      }

      // Watch container size changes natively
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
      this.resizeObserver = new ResizeObserver(() => {
        this.onResize();
      });
      this.resizeObserver.observe(container);

      // Force proper canvas dimensions
      this.onResize();

      // Ensure at least 2 animation frames have completed so WebGL has drawn pixels to canvas
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
      });

      if (generation !== this.globeInitGeneration) return;

      // Verify canvas exists and is rendered
      const canvas = container.querySelector('canvas');
      if (canvas && (canvas.width === 0 || canvas.height === 0)) {
        this.onResize();
      }

      // Initialize flying planes
      this.initFlyingPlanes();

      // Globe is confirmed 100% ready and rendered on screen -> Dismiss loader smoothly
      this.ngZone.run(() => {
        this.isGlobeLoading = false;
        this.cdr.detectChanges();
      });

    } catch (err) {
      console.warn('Globe initialization error:', err);
      // Failsafe timeout only in case of complete resource failure
      setTimeout(() => {
        if (generation === this.globeInitGeneration && this.isGlobeLoading) {
          this.ngZone.run(() => {
            this.isGlobeLoading = false;
            this.cdr.detectChanges();
          });
        }
      }, 8000);
    }
  }

  private initFlyingPlanes(): void {
    const three = (window as any).THREE || (typeof THREE !== 'undefined' ? THREE : null);
    if (!this.globe || !three) return;

    if (this.planeAnimFrameId) {
      cancelAnimationFrame(this.planeAnimFrameId);
      this.planeAnimFrameId = null;
    }

    if (this.planesGroup) {
      try {
        this.globe.scene().remove(this.planesGroup);
      } catch (e) { }
      this.planesGroup = null;
    }

    this.planesGroup = new three.Group();
    this.planeObjects = [];
    this.activeFlights = [];
    this.currentFlightRouteIndex = 0;
    this.lastFlightLaunchTime = 0;

    // Create a pool of 6 plane meshes so 3-4 flights can be airborne concurrently
    for (let i = 0; i < 6; i++) {
      const mesh = this.createAirplaneMesh(this.isDarkMode);
      if (mesh) {
        mesh.visible = false;
        this.planesGroup.add(mesh);
        this.planeObjects.push({ mesh, inUse: false });
      }
    }

    if (this.planeObjects.length > 0) {
      this.globe.scene().add(this.planesGroup);

      // Pre-seed 3 staggered flights immediately on start so 3-4 planes are active right away
      const now = performance.now();
      const mumbaiLat = 19.0760;
      const mumbaiLng = 72.8777;
      const pMumbai = this.globe.getCoords(mumbaiLat, mumbaiLng, 0);
      const flightTargets = this.locations.filter(l => l.id !== 'mumbai');

      if (pMumbai && flightTargets.length > 0) {
        const v1 = new three.Vector3(pMumbai.x, pMumbai.y, pMumbai.z).normalize();
        const initialSeeds = [
          { offset: 3600 }, // ~60% through flight
          { offset: 1800 }, // ~30% through flight
          { offset: 0 }     // taking off now
        ];

        initialSeeds.forEach((seed, idx) => {
          const loc = flightTargets[idx % flightTargets.length];
          const pDest = this.globe.getCoords(loc.lat, loc.lng, 0);
          const idlePlane = this.planeObjects[idx];

          if (pDest && idlePlane) {
            const v2 = new three.Vector3(pDest.x, pDest.y, pDest.z).normalize();
            const angle = v1.angleTo(v2);
            if (angle > 0.03) {
              idlePlane.inUse = true;
              const dist = Math.hypot(loc.lat - mumbaiLat, loc.lng - mumbaiLng);
              const duration = Math.min(6800, Math.max(4800, dist * 25 + 4600));
              const maxAlt = Math.min(0.038, Math.max(0.015, dist * 0.00035 + 0.012));

              this.activeFlights.push({
                plane: idlePlane,
                v1,
                v2,
                angle,
                maxAlt,
                startTime: now - seed.offset,
                duration,
                id: loc.id
              });
            }
          }
        });

        this.currentFlightRouteIndex = initialSeeds.length % flightTargets.length;
        this.lastFlightLaunchTime = now;
      }

      this.animatePlanes();
    }
  }

  private animatePlanes(): void {
    const three = (window as any).THREE || (typeof THREE !== 'undefined' ? THREE : null);
    if (!this.globe || !three || !this.planeObjects.length) return;

    const now = performance.now();
    const R = (typeof this.globe.getGlobeRadius === 'function' ? this.globe.getGlobeRadius() : 100) || 100;
    const launchInterval = 1500; // Launch every 1.5s to maintain 3-4 planes in the air at all times
    const flightTargets = this.locations.filter(l => l.id !== 'mumbai');

    // Dispatch next destination flight when interval elapses
    if (now - this.lastFlightLaunchTime >= launchInterval && flightTargets.length > 0) {
      const idlePlane = this.planeObjects.find(p => !p.inUse);
      if (idlePlane) {
        const loc = flightTargets[this.currentFlightRouteIndex % flightTargets.length];
        this.currentFlightRouteIndex = (this.currentFlightRouteIndex + 1) % flightTargets.length;

        const mumbaiLat = 19.0760;
        const mumbaiLng = 72.8777;
        const pMumbai = this.globe.getCoords(mumbaiLat, mumbaiLng, 0);
        const pDest = this.globe.getCoords(loc.lat, loc.lng, 0);

        if (pMumbai && pDest) {
          const v1 = new three.Vector3(pMumbai.x, pMumbai.y, pMumbai.z).normalize();
          const v2 = new three.Vector3(pDest.x, pDest.y, pDest.z).normalize();
          const angle = v1.angleTo(v2);

          if (angle > 0.03) {
            idlePlane.inUse = true;
            this.lastFlightLaunchTime = now;

            const dist = Math.hypot(loc.lat - mumbaiLat, loc.lng - mumbaiLng);
            const duration = Math.min(6800, Math.max(4800, dist * 25 + 4600));
            const maxAlt = Math.min(0.038, Math.max(0.015, dist * 0.00035 + 0.012));

            this.activeFlights.push({
              plane: idlePlane,
              v1,
              v2,
              angle,
              maxAlt,
              startTime: now,
              duration,
              id: loc.id
            });
          }
        }
      }
    }

    // Animate airborne flights
    for (let i = this.activeFlights.length - 1; i >= 0; i--) {
      const f = this.activeFlights[i];
      const elapsed = now - f.startTime;
      const progress = elapsed / f.duration;

      if (progress >= 1.0) {
        // Touchdown at destination marker complete
        f.plane.mesh.visible = false;
        f.plane.inUse = false;
        this.activeFlights.splice(i, 1);
        continue;
      }

      const pos = this.calcArcPoint(f.v1, f.v2, f.angle, f.maxAlt, progress, R);
      const radialUp = pos.clone().normalize();

      // Great-circle orbit plane normal
      const orbitNormal = new three.Vector3().crossVectors(f.v1, f.v2).normalize();

      // Horizontal flight forward tangent along spherical Earth surface towards destination
      // (orbitNormal x radialUp) is mathematically guaranteed to be:
      // 1. Strictly perpendicular to radialUp (surfaceForward . radialUp === 0)
      // 2. Exactly pointing in the direction of flight towards the destination marker
      // 3. Perfectly aligned with the great circle path all the way to touchdown
      let surfaceForward = new three.Vector3().crossVectors(orbitNormal, radialUp).normalize();
      if (surfaceForward.lengthSq() < 0.001) {
        surfaceForward = f.v2.clone().sub(radialUp).normalize();
      }

      // Aircraft Pitch Attitude:
      // - Takeoff (progress < 0.18): gentle climb pitch (+3° to +5° nose-up)
      // - Cruise (0.18 <= progress <= 0.78): level flight with subtle positive angle of attack
      // - Landing Flare (progress > 0.78): as the aircraft touches down onto the marker,
      //   the nose flares gently UP (+3° to +4°), touching down rear-gear first, NEVER DIVING!
      let pitchUp = 0.015;
      if (progress < 0.18) {
        pitchUp = 0.015 + 0.05 * (1 - progress / 0.18);
      } else if (progress > 0.78) {
        const flareProgress = (progress - 0.78) / 0.22;
        pitchUp = 0.015 + 0.045 * flareProgress;
      }

      const forward = new three.Vector3()
        .addVectors(surfaceForward, radialUp.clone().multiplyScalar(pitchUp))
        .normalize();

      // Wing lateral axis (parallel to Earth surface)
      const wingRight = new three.Vector3().crossVectors(forward, radialUp).normalize();
      // True Up vector (roof pointing away from Earth surface into space)
      const trueUp = new three.Vector3().crossVectors(wingRight, forward).normalize();

      // Basis matrix: X = wingRight, Y = trueUp, Z = -forward (nose along -Z)
      const rotMatrix = new three.Matrix4();
      rotMatrix.makeBasis(wingRight, trueUp, forward.clone().negate());
      f.plane.mesh.quaternion.setFromRotationMatrix(rotMatrix);
      f.plane.mesh.position.copy(pos);

      // Smooth takeoff climb, steady cruising scale, and touchdown fade right at destination marker
      let scale = 0.62;
      let opacity = 1.0;

      if (progress < 0.14) {
        // Takeoff climb & fade in from Mumbai
        const takeOffProgress = progress / 0.14;
        scale = 0.62 * (0.35 + 0.65 * takeOffProgress);
        opacity = takeOffProgress;
      } else if (progress > 0.86) {
        // Touchdown directly onto destination marker core:
        // Plane completes journey and dissolves smoothly right into the destination marker beacon
        const touchProgress = (progress - 0.86) / 0.14;
        opacity = Math.max(0, 1.0 - touchProgress);
        scale = 0.62 * (1.0 - 0.35 * touchProgress);
      }

      f.plane.mesh.scale.set(scale, scale, scale);
      f.plane.mesh.traverse((child: any) => {
        if (child.isMesh && child.material) {
          child.material.opacity = opacity;
        }
      });
      f.plane.mesh.visible = opacity > 0.01;
    }

    this.planeAnimFrameId = requestAnimationFrame(() => this.animatePlanes());
  }

  private calcArcPoint(v1: any, v2: any, angle: number, maxAlt: number, t: number, R: number): any {
    const three = (window as any).THREE || (typeof THREE !== 'undefined' ? THREE : null);
    if (!three) return v1;

    const sinAngle = Math.sin(angle);
    if (sinAngle < 0.0001) return v1.clone().multiplyScalar(R);

    const w1 = Math.sin((1 - t) * angle) / sinAngle;
    const w2 = Math.sin(t * angle) / sinAngle;

    const currentDir = new three.Vector3()
      .addVectors(v1.clone().multiplyScalar(w1), v2.clone().multiplyScalar(w2))
      .normalize();

    // Smooth Continuous Altitude Profile:
    // 1. Takeoff (t: 0 -> 0.18): smooth cosine climb from ground (0) to cruising altitude maxAlt
    // 2. Cruise (t: 0.18 -> 0.76): steady cruise at maxAlt
    // 3. Descent & Flare (t: 0.76 -> 1.0): smooth cosine descent from maxAlt to ground (0),
    //    leveling off flat at touchdown (slope = 0 at t = 1.0) directly onto the destination marker!
    let alt: number;
    if (t < 0.18) {
      const s = t / 0.18;
      alt = maxAlt * (1 - Math.cos(s * Math.PI)) * 0.5;
    } else if (t <= 0.76) {
      alt = maxAlt;
    } else {
      const s = (t - 0.76) / 0.24;
      alt = maxAlt * (1 + Math.cos(s * Math.PI)) * 0.5;
    }

    return currentDir.multiplyScalar(R * (1 + alt));
  }

  private createAirplaneMesh(isDark: boolean): any {
    const three = (window as any).THREE || (typeof THREE !== 'undefined' ? THREE : null);
    if (!three) return null;

    const planeGroup = new three.Group();

    const bodyColor = isDark ? 0xffffff : 0x0f172a;
    const wingColor = isDark ? 0x00ffff : 0x0284c7;
    const accentColor = isDark ? 0xffcc03 : 0xf59e0b;
    const glowColor = isDark ? 0x00ffff : 0x38bdf8;

    const matBody = new three.MeshStandardMaterial({
      color: bodyColor,
      roughness: 0.25,
      metalness: 0.3,
      transparent: true,
      opacity: 1.0
    });
    const matWings = new three.MeshStandardMaterial({
      color: wingColor,
      roughness: 0.2,
      metalness: 0.4,
      transparent: true,
      opacity: 1.0
    });
    const matAccent = new three.MeshBasicMaterial({ color: accentColor, transparent: true, opacity: 1.0 });
    const matEngineGlow = new three.MeshBasicMaterial({ color: glowColor, transparent: true, opacity: 1.0 });

    // 1. Fuselage - streamlined body (pointing along -Z)
    // Main cabin cylinder: radius 0.6, length 3.2
    const cabinGeom = new three.CylinderGeometry(0.55, 0.6, 3.2, 12);
    cabinGeom.rotateX(Math.PI / 2);
    const cabin = new three.Mesh(cabinGeom, matBody);
    cabin.position.set(0, 0, 0);
    planeGroup.add(cabin);

    // Nose cone: tapered forward along -Z
    const noseGeom = new three.ConeGeometry(0.55, 1.4, 12);
    noseGeom.rotateX(-Math.PI / 2);
    const nose = new three.Mesh(noseGeom, matBody);
    nose.position.set(0, -0.05, -2.3);
    planeGroup.add(nose);

    // Windshield cockpit visor
    const glassGeom = new three.BoxGeometry(0.65, 0.25, 0.55);
    glassGeom.rotateX(-0.3);
    const glass = new three.Mesh(glassGeom, matAccent);
    glass.position.set(0, 0.3, -1.9);
    planeGroup.add(glass);

    // Tail cone: tapered backward along +Z
    const tailConeGeom = new three.ConeGeometry(0.6, 1.6, 12);
    tailConeGeom.rotateX(Math.PI / 2);
    const tailCone = new three.Mesh(tailConeGeom, matBody);
    tailCone.position.set(0, 0.1, 2.4);
    planeGroup.add(tailCone);

    // 2. Swept Main Wings (Left and Right wings using BoxGeometry)
    // Left Wing (swept back)
    const leftWingGeom = new three.BoxGeometry(3.6, 0.1, 1.2);
    const leftWing = new three.Mesh(leftWingGeom, matWings);
    leftWing.position.set(-2.0, -0.05, 0.15);
    leftWing.rotation.set(0, 0.35, -0.05); // Swept back
    planeGroup.add(leftWing);

    // Right Wing (swept back)
    const rightWingGeom = new three.BoxGeometry(3.6, 0.1, 1.2);
    const rightWing = new three.Mesh(rightWingGeom, matWings);
    rightWing.position.set(2.0, -0.05, 0.15);
    rightWing.rotation.set(0, -0.35, 0.05); // Swept back
    planeGroup.add(rightWing);

    // Winglets at tips (upturned wing tips)
    const leftWingletGeom = new three.BoxGeometry(0.08, 0.7, 0.45);
    const leftWinglet = new three.Mesh(leftWingletGeom, matAccent);
    leftWinglet.position.set(-3.7, 0.3, 0.75);
    planeGroup.add(leftWinglet);

    const rightWingletGeom = new three.BoxGeometry(0.08, 0.7, 0.45);
    const rightWinglet = new three.Mesh(rightWingletGeom, matAccent);
    rightWinglet.position.set(3.7, 0.3, 0.75);
    planeGroup.add(rightWinglet);

    // 3. Twin Turbofan Jet Engines under wings
    const engineGeom = new three.CylinderGeometry(0.28, 0.24, 1.0, 10);
    engineGeom.rotateX(Math.PI / 2);
    const leftEngine = new three.Mesh(engineGeom, matBody);
    leftEngine.position.set(-1.5, -0.4, -0.2);
    planeGroup.add(leftEngine);

    const rightEngine = new three.Mesh(engineGeom, matBody);
    rightEngine.position.set(1.5, -0.4, -0.2);
    planeGroup.add(rightEngine);

    // Engine exhaust glow (back of engines at +Z)
    const glowGeom = new three.SphereGeometry(0.22, 8, 8);
    const leftGlow = new three.Mesh(glowGeom, matEngineGlow);
    leftGlow.position.set(-1.5, -0.4, 0.35);
    planeGroup.add(leftGlow);

    const rightGlow = new three.Mesh(glowGeom, matEngineGlow);
    rightGlow.position.set(1.5, -0.4, 0.35);
    planeGroup.add(rightGlow);

    // 4. Vertical Tail Fin (Swept back fin pointing +Y)
    const finGeom = new three.BoxGeometry(0.12, 1.6, 1.3);
    const fin = new three.Mesh(finGeom, matWings);
    fin.position.set(0, 0.95, 2.1);
    fin.rotation.set(-0.35, 0, 0); // Swept back
    planeGroup.add(fin);

    // Fin tip accent
    const finTipGeom = new three.BoxGeometry(0.14, 0.35, 0.65);
    const finTip = new three.Mesh(finTipGeom, matAccent);
    finTip.position.set(0, 1.6, 2.4);
    finTip.rotation.set(-0.35, 0, 0);
    planeGroup.add(finTip);

    // 5. Horizontal Tail Stabilizers
    const hStabGeom = new three.BoxGeometry(2.4, 0.08, 0.7);
    const hStab = new three.Mesh(hStabGeom, matWings);
    hStab.position.set(0, 0.35, 2.5);
    hStab.rotation.set(0, 0, 0);
    planeGroup.add(hStab);

    planeGroup.scale.set(0.65, 0.65, 0.65);
    return planeGroup;
  }

  onResize(): void {
    if (this.globeContainer && this.globe) {
      const container = this.globeContainer.nativeElement;
      let width = container.clientWidth;
      let height = container.clientHeight || window.innerHeight;

      // Ensure dimensions are positive during rapid route transitions
      if (width <= 0) {
        width = window.innerWidth <= 768 ? window.innerWidth : Math.round(window.innerWidth * 0.62);
      }
      if (height <= 0) {
        height = window.innerHeight;
      }

      this.globe.width(width).height(height);
    }
  }

  onLocationClick(loc: any): void {
    this.selectedLocation = loc;
    this.updateSelectedMarkerUI();
    if (this.globe) {
      if (this.globe.controls()) {
        this.globe.controls().autoRotate = false;
      }
      // Keep mobile zoom slightly further out when clicking a location
      const zoomAlt = window.innerWidth <= 768 ? 2.0 : 1.5;
      this.globe.pointOfView({ lat: loc.lat, lng: loc.lng, altitude: zoomAlt }, 1000);
    }
  }

  updateSelectedMarkerUI(): void {
    if (typeof document === 'undefined') return;
    const markerEls = document.querySelectorAll('.location-marker');
    markerEls.forEach((el: any) => {
      const markerId = el.getAttribute('data-id');
      if (this.selectedLocation && markerId === this.selectedLocation.id) {
        el.classList.add('is-selected');
      } else {
        el.classList.remove('is-selected');
      }
    });
  }

  surpriseMe(): void {
    if (this.isSpinningSurprise || !this.locations || !this.locations.length) return;

    this.isSpinningSurprise = true;
    this.userManuallyInteracted = true;

    // Pick a random location (avoiding current one if already selected)
    let candidates = this.locations;
    if (this.selectedLocation) {
      candidates = this.locations.filter(l => l.id !== this.selectedLocation.id);
    }
    const randomLoc = candidates[Math.floor(Math.random() * candidates.length)] || this.locations[0];

    // Disable auto-rotate
    const ctrl = this.globe?.controls();
    if (ctrl) {
      ctrl.autoRotate = false;
    }

    if (this.globe) {
      const currentPov = this.globe.pointOfView() || { lat: 21, lng: 78, altitude: 2.0 };
      // Step 1: Fast exhilarating spin across the globe
      const spinLng = (currentPov.lng + 150) % 360;
      this.globe.pointOfView({ lat: 18, lng: spinLng, altitude: 2.6 }, 500);

      // Step 2: Swoop into the chosen random location
      setTimeout(() => {
        const targetAlt = window.innerWidth <= 768 ? 2.0 : 1.5;
        this.globe.pointOfView({ lat: randomLoc.lat, lng: randomLoc.lng, altitude: targetAlt }, 1100);
      }, 550);

      // Step 3: Select location, show card, and activate selected marker pulse
      setTimeout(() => {
        this.ngZone.run(() => {
          this.selectedLocation = randomLoc;
          this.isSpinningSurprise = false;
          this.updateSelectedMarkerUI();
          this.cdr.detectChanges();
        });
      }, 1700);
    } else {
      this.selectedLocation = randomLoc;
      this.isSpinningSurprise = false;
      this.updateSelectedMarkerUI();
      this.cdr.detectChanges();
    }
  }

  selectGlobal(): void {
    this.selectedLocation = {
      id: 'global',
      name: 'Any Destination Worldwide',
      lat: 20,
      lng: 0
    };
    this.updateSelectedMarkerUI();
    if (this.globe) {
      if (this.globe.controls()) {
        this.globe.controls().autoRotate = true;
        this.globe.controls().autoRotateSpeed = 0.6;
      }
      this.globe.pointOfView({ lat: 20, lng: 0, altitude: 2.2 }, 1000);
    }
  }

  proceedToSignup(destId?: string): void {
    const targetId = destId || (this.selectedLocation ? this.selectedLocation.id : 'global');
    const destination = this.destinationVideoService.resolveDestination(targetId);

    // Update video service state
    this.destinationVideoService.setDestination(destination.id);

    // Build query params: forward all existing params + add clean short destination code 'd'
    const queryParams = {
      ...this.queryParams,
      d: destination.code
    };

    this.router.navigate(['/assist-journey/sign-up'], { queryParams });
  }

  ngOnDestroy(): void {
    if (typeof window !== 'undefined') {
      if (this.pageShowListener) {
        window.removeEventListener('pageshow', this.pageShowListener);
        this.pageShowListener = null;
      }
      if (this.popStateListener) {
        window.removeEventListener('popstate', this.popStateListener);
        this.popStateListener = null;
      }
    }
    if (this.typewriterTimeout) {
      clearTimeout(this.typewriterTimeout);
    }
    if (this.planeAnimFrameId) {
      cancelAnimationFrame(this.planeAnimFrameId);
      this.planeAnimFrameId = null;
    }
    if (this.planesGroup && this.globe) {
      try {
        this.globe.scene().remove(this.planesGroup);
      } catch (e) { }
      this.planesGroup = null;
    }
    if (this.stopRotationListener && this.globeContainer) {
      const el = this.globeContainer.nativeElement;
      el.removeEventListener('pointerdown', this.stopRotationListener);
      el.removeEventListener('touchstart', this.stopRotationListener);
      el.removeEventListener('mousedown', this.stopRotationListener);
      if (this.hoverEnterListener) {
        el.removeEventListener('mouseenter', this.hoverEnterListener);
      }
      if (this.hoverLeaveListener) {
        el.removeEventListener('mouseleave', this.hoverLeaveListener);
      }
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.globe) {
      try {
        this.globe._destructor();
      } catch (e) { }
      this.globe = null;
    }
    if (typeof document !== 'undefined' && document.body) {
      document.body.classList.remove('globe-dark-theme', 'globe-light-theme');
    }
  }
}
